import { parseISO } from 'date-fns'
import type { FC } from 'react'
import { useContext, useState } from 'react'
import styled from 'styled-components'

import Checkbox from '@/components/Checkbox'
import { Button, AlternateButton } from '@/components/DEPRECATED_UI/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import H2 from '@/components/H2'
import DateInput from '@/components/input/DateInput'
import TextInput from '@/components/input/TextInput'
import ctx from '@/context/AppContext'
import { toErrorNode, useVerifyAddressMutation } from '@/lib/apollo'
import { VerifiedState } from '@/lib/apollo/mutation/verify-address'
import type { Address } from '@/types/Activity'
import { formatDateISO } from '@/utils/date'

const ContentContainer = styled.div`
  display: grid;
  gap: 20px;
  margin-top: 20px;
`

const TenancyStartDateContainer = styled.div`
  display: grid;
  grid-gap: 10px;
`

interface Props {
  customerId: string
  subjectId: string
  onClose: () => void
  address: Address
}

const VerifySidebar: FC<Props> = ({ customerId, subjectId, onClose, address }) => {
  const [verifyAddress, { data, loading, error }] = useVerifyAddressMutation()

  const [isVerifiedByDocAndTenancyAgreement, setIsVerifiedByDocAndTenancyAgreement] =
    useState<boolean>(false)
  const [startDate, setStartDate] = useState<Date | null>(
    parseISO(address.startDate || ''),
  )
  const [weeksNotice, setWeeksNotice] = useState<number | null>(null)
  const [numberOfTimesLate, setNumberOfTimesLate] = useState<number | null>(null)

  const { personId } = useContext(ctx)

  const handleSubmit = () =>
    verifyAddress({
      variables: {
        customerId,
        subjectId,
        userId: personId ?? '',
        state: VerifiedState.verifiedByDocumentsAndAst,
        startDate: formatDateISO(startDate as Date),
        weeksNotice: weeksNotice as number,
        numberOfTimesLate: numberOfTimesLate as number,
      },
    })

  return (
    <SlideInSidebar
      onClose={onClose}
      header={<H2>Verify address</H2>}
      content={
        <ContentContainer>
          <Checkbox
            label="I have verified via bank statements and Tenancy Agreement"
            checked={isVerifiedByDocAndTenancyAgreement}
            onChange={() =>
              setIsVerifiedByDocAndTenancyAgreement(!isVerifiedByDocAndTenancyAgreement)
            }
          />
          {isVerifiedByDocAndTenancyAgreement && (
            <>
              <TenancyStartDateContainer>
                <div>Tenancy start date:</div>
                <DateInput date={startDate} setDate={date => setStartDate(date)} />
              </TenancyStartDateContainer>
              <TextInput
                type="number"
                onChange={e => {
                  const value = e.target.value as unknown as string // prettier-ignore
                  setWeeksNotice(parseInt(value))
                }}
                value={weeksNotice}
                label="Weeks notice"
              />
              <TextInput
                type="number"
                onChange={e => {
                  const value = e.target.value as unknown as string // prettier-ignore
                  setNumberOfTimesLate(parseInt(value))
                }}
                value={numberOfTimesLate}
                label="Number of times late"
              />
            </>
          )}
          {toErrorNode(error)}
        </ContentContainer>
      }
      footer={
        <div className="side-by-side-flex-end">
          <AlternateButton onClick={onClose}>Cancel</AlternateButton>
          {!data && (
            <Button
              disabled={
                !isVerifiedByDocAndTenancyAgreement ||
                startDate === null ||
                weeksNotice === null ||
                loading
              }
              onClick={handleSubmit}
            >
              Confirm
            </Button>
          )}
        </div>
      }
    />
  )
}

export default VerifySidebar
